import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { ShortcutKbd } from "@repo/ui/lib/actions/shortcut-kbd";
import { cn } from "@repo/ui/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { Button } from "@repo/ui/ui/button";
import { SidebarTrigger, useSidebar } from "@repo/ui/ui/sidebar";
import { createLink, useMatch, useRouterState } from "@tanstack/react-router";

import { AiPaneToggle } from "~/components/app/ai-pane-toggle";
import { BoardShareDialog } from "~/components/app/board-share-dialog";
import { BoardOptions } from "~/components/app/sidebar/content/board-options";
import { ThreadOptions } from "~/components/app/sidebar/content/thread-options";
import { DatasetOptions } from "~/components/dataset-options";
import { OrgAvatar } from "~/components/org-avatar";
import { useSidebarSync } from "~/hooks/sidebar-sync.hook";
import { useBoardViewMode } from "~/pages/board/board-session";
import { DatasetsHeaderActions } from "~/pages/datasets/datasets-header-actions";
import { user$ } from "~/stores/auth";
import { hostOrgs$ } from "~/stores/board-snapshot";
import { OrgHeader } from "./org-header";
import { PageTitle } from "./page-title";
import { ThreadCollapseButton } from "./thread-collapse-button";

const ButtonLink = createLink(Button);

export const TopBar = () => {
  const userId = useValue(user$.id);
  const isChromelessPage = useRouterState({
    select: (s) =>
      s.location.pathname === "/login" ||
      // OAuth consent / org-selection interstitials render full-screen with no
      // app chrome, like the auth pages.
      s.location.pathname.startsWith("/oauth/"),
  });
  const isAppRouteMatch = useRouterState({
    select: (s) => s.matches.some((match) => match.id === "/_app/"),
  });
  const isSettingsPage = useRouterState({
    select: (s) => s.location.pathname.startsWith("/settings"),
  });
  const rawBoardMatch = useMatch({
    from: "/boards/$boardId",
    shouldThrow: false,
  });
  const boardMatch =
    rawBoardMatch?.status === "notFound" ? undefined : rawBoardMatch;
  const datasetMatch = useMatch({
    from: "/_app/datasets/$datasetId",
    shouldThrow: false,
  });
  const datasetsBrowseMatch = useMatch({
    from: "/_app/datasets/",
    shouldThrow: false,
  });
  const threadMatch = useMatch({
    from: "/_app/thread/$threadId",
    shouldThrow: false,
  });
  // The board session provider re-exposes the same viewMode the route's
  // `beforeLoad` resolved. Read from there so the value is available
  // synchronously on first render — same guarantee as reading from the
  // route match's context.
  const boardViewMode = useBoardViewMode();
  const isReadOnlyBoard =
    boardViewMode === "readonly-public" || boardViewMode === "readonly-member";
  // `/boards/$boardId` was moved out of `/_app/` to host the read-only
  // viewer, but app-mode boards still render the `<App>` shell (via
  // `<AppShell>`) with the sidebar. Treat them as app pages so the org chip +
  // sidebar trigger expand/collapse correctly with the sidebar state.
  const isAppPage = isAppRouteMatch || boardViewMode === "app";

  useSidebarSync();

  const { open: sidebarOpen } = useSidebar("left");
  const navpaneVisible = isAppPage && sidebarOpen && !isReadOnlyBoard;

  if (isChromelessPage) return null;
  return (
    <header
      className={cn(
        "flex h-(--header-height) w-full shrink-0 items-center px-2",
        navpaneVisible ? "gap-5" : "gap-0",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center gap-1 transition-[width] duration-200 ease-linear",
          isReadOnlyBoard
            ? "md:w-auto"
            : navpaneVisible
              ? "md:w-[calc(var(--sidebar-width)-var(--spacing)*5)]"
              : "md:w-16",
        )}
      >
        {isReadOnlyBoard && boardMatch ? (
          <ReadOnlyOrgHeader boardId={boardMatch.params.boardId} />
        ) : userId ? (
          <OrgHeader collapsed={!navpaneVisible} />
        ) : (
          <ButtonLink to="/login" variant="ghost">
            <Icon.Logo name="mora" className="-ml-1 size-6" />
            Mora
          </ButtonLink>
        )}
        {isAppPage && userId && !isSettingsPage && !isReadOnlyBoard && (
          <Tooltip>
            <TooltipTrigger render={<SidebarTrigger name="left" />} />
            <TooltipContent className="flex items-center gap-2">
              Toggle sidebar
              <ShortcutKbd shortcutId="toggle-sidebar" />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex min-w-0 shrink grow items-center gap-2">
        <PageTitle />
        {isReadOnlyBoard ? (
          <div className="mr-2 ml-auto flex items-center gap-2">
            <ReadOnlyBadge variant={boardViewMode} />
            {!userId && (
              <ButtonLink to="/login" variant="ghost">
                Sign in
              </ButtonLink>
            )}
          </div>
        ) : userId ? (
          <div className="mr-2 ml-auto flex items-center gap-1">
            {boardMatch && (
              <BoardShareDialog boardId={boardMatch.params.boardId}>
                <Button variant="ghost" size="icon-sm">
                  <Icon name="link" className="size-3.5" />
                </Button>
              </BoardShareDialog>
            )}
            {boardMatch && (
              <BoardOptions
                boardId={boardMatch.params.boardId}
                showShare={false}
              >
                <Button variant="ghost" size="icon-sm">
                  <Icon name="ellipsis-horizontal" />
                </Button>
              </BoardOptions>
            )}
            {datasetMatch && (
              <DatasetOptions
                datasetId={datasetMatch.params.datasetId}
                type="dropdown"
              >
                <Button variant="ghost" size="icon-sm">
                  <Icon name="ellipsis-horizontal" />
                </Button>
              </DatasetOptions>
            )}
            {datasetsBrowseMatch && <DatasetsHeaderActions />}
            {threadMatch && (
              <>
                <ThreadCollapseButton />
                <ThreadOptions threadId={threadMatch.params.threadId}>
                  <Button variant="ghost" size="icon-sm">
                    <Icon name="ellipsis-horizontal" />
                  </Button>
                </ThreadOptions>
              </>
            )}
            <AiPaneToggle />
          </div>
        ) : (
          <ButtonLink to="/login" className="mr-2 ml-auto">
            Sign in
          </ButtonLink>
        )}
      </div>
    </header>
  );
};

/**
 * Top-left org chip for the read-only board viewer. Shows the **host** org
 * (board owner) from `hostOrgs$` — never the viewer's `activeOrganization$`.
 * Static (no dropdown / hover), but matches `OrgHeader`'s ghost-button-`lg`
 * height + padding so it has the same visual weight as the editor.
 * Renders nothing until the snapshot resolves so we don't flash a stale chip.
 */
const ReadOnlyOrgHeader = ({ boardId }: { boardId: string }) => {
  const orgName = useValue(() => hostOrgs$[boardId]?.name.get());
  const orgLogo = useValue(() => hostOrgs$[boardId]?.logo.get());

  if (orgName === undefined) return null;

  return (
    <div className="flex h-9 shrink-0 items-center gap-2 px-2">
      <OrgAvatar name={orgName} logo={orgLogo} className="size-5" />
      <span className="min-w-0 truncate font-medium text-foreground text-sm">
        {orgName || "\u00a0"}
      </span>
    </div>
  );
};

const ReadOnlyBadge = ({
  variant,
}: {
  variant: "readonly-public" | "readonly-member";
}) => (
  <span className="rounded-md border bg-muted/40 px-1.5 py-0.5 text-muted-foreground text-xs">
    {variant === "readonly-member" ? "Shared with you" : "Read-only"}
  </span>
);
